/* eslint-disable ember/classic-decorator-hooks */
import EmberObject, { set } from '@ember/object';

import * as CircuitUtils from '../utils/circuit';
import * as ResistorToleranceUtils from '../tolerances/resistor-tolerance-utils';
import * as CommonUtils from '../../utils/common';
import { computeNominalValue } from '../tolerances/tolerance-utils';

//
// The idea behind a these components (capacitor, resistor, inductor) is we have classes that encapsulate
// how each of them are created.
// R4 is a gain resistor. Meaning if the specs requires gain,
// r3 and r4 are added in the circuit. Not all circuits support gains.
// r4's value is based off from r3 value.
//
// Lifecycle: This component is created with the
// 1. ideal value (component value based from computation, aka. textbook component in the old app, or ideal value),
// 2. a multiplier to get the minimum limit.
// 3. a multiplier to get the maximum limit.
//
// R4 is almost the same as r3. This however, only gets the closest nominal. It doesn't check whether it's beyond the limits.
//
// Construction: R4.create({idealValue: idealValue, resistorMaximumLimitMultiplier: resistorMaximumLimitMultiplier, resistorMinimumLimitMultiplier: resistorMinimumLimitMultiplier})
// Hash:
// hash = {
//              idealValue: { number },
//              maximumLimitMultipler: { number },
//              resistorMinimumLimitMultiplier: { number }
//        }
// Create: Capacitor.create(hash);
// @inputProperty { number } idealValue - The computed value based from a given c1.
// @inputProperty { number } resistorMaximumLimitMultiplier - A value computed from the ideal values of all the components. Used to compute this component's maximum limit.
// @inputProperty { number } resistorMinimumLimitMultiplier - A value computed from the ideal values of all the components used to compute this component's minimum limit.

// @getterProperty { number } nominalValue - The computed commercial value. This matches the value of capacitors that are available in the market.
// @getterProperty { number[] } toleranceArray - The nominal value multiplied to each of the elements of the array. The array consists of different tolerance multipliers.
// @getterProperty { FilterConstants.capacitorTolerance } finalTolerance - If the desired tolerance fails to get the nominal, there's a process that goes through all the available tolerance options to get the nominal value.
// @getterProperty { FilterConstants.capacitorTolerance } desiredTolerance - the selected capacitor tolerance or the default if none.
// @getterProperty { bool } isCommercial - True if this component is already converted into a commercial capacitor. False if not.

// @computed { number } baseResistorValue - if the idealValue is given, it is returned as baseResistorValue else, if we're given a
// r3Value and a gain, we can use them instead.
// @computed { number } maximum - the capacitor instance's maximum limit
// @computed { number } minimum - the capacitor instance's minimum limit
// @computed { number } value - the ideal value if the component is not commercial, the nominal value if the component is already converted to a commercial one.
// @method { this } toCommercialComponent() - converts the component into its commercial version.
// @method { number } getNominalValue() - gets the nominal value. Can only be accessed when the component is already a commercial one.

export default class R4 extends EmberObject {
  idealValue = null;
  r3Value = null;
  gain = null;
  resistorMaximumLimitMultiplier = null;
  resistorMinimumLimitMultiplier = null;
  nominalValue = null;
  toleranceArray = null;
  finalTolerance = null;
  isCommercial = null;
  init() {
    super.init();
    set(this, 'isCommercial', false);
    let baseResistorValue = CommonUtils.isNullOrUndefined(this.idealValue)
      ? this.r3Value * (this.gain - 1)
      : this.idealValue;
    this.minimum =
      CommonUtils.isNullOrUndefined(this.minimum) || this.minimum === 0
        ? CircuitUtils.computeGainResistorLimit(baseResistorValue, this.resistorMinimumLimitMultiplier)
        : this.minimum;
    this.maximum =
      CommonUtils.isNullOrUndefined(this.maximum) || this.maximum === 0
        ? CircuitUtils.computeGainResistorLimit(baseResistorValue, this.resistorMaximumLimitMultiplier)
        : this.maximum;
    this.value = baseResistorValue;
  }

  toCommercialComponent(toleranceInput) {
    set(this, 'toleranceInput', toleranceInput);
    let tolInput = !toleranceInput ? CircuitUtils.createToleranceInputDefaults() : toleranceInput;
    // let nominalValue = ResistorToleranceUtils.computeNominalValue(this.idealValue, tolInput, 'closest');
    let nominalValue = computeNominalValue(this.idealValue, tolInput.resistorPreferredSeries, 'closest');
    let toleranceValues = ResistorToleranceUtils.getToleranceValues(nominalValue, tolInput.resistorTolerance);

    set(this, 'value', nominalValue);
    set(this, 'toleranceArray', toleranceValues.toleranceArray);
    set(this, 'finalTolerance', toleranceValues.finalTolerance);
    set(this, 'desiredTolerance', tolInput.resistorTolerance);
    set(this, 'isCommercial', true);

    return this;
  }

  getNominalValue() {
    if (!this.isCommercial) throw 'component is not yet commercial. Call toCommercialComponent() first.';

    return this.value;
  }
}
